import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Expand, X } from 'lucide-react';
import './filmBoxes.scss';

// فیلمِ طرح در صفحه‌ی «ساخت فایل جدید»: به‌جای یک فیلمِ بزرگ، چند باکسِ کوچک — هر باکس یک «نما» (زاویه) از همان
// فیلم را تکرار می‌کند. اگر نماها بیشتر از باکس‌ها باشند، هر باکس نماهای سهمِ خودش را به نوبت نشان می‌دهد.
// هر باکس دکمه‌ی بزرگ‌نمایی دارد. segments: [{ id, label, t0, t1 }] (filmRecorder.jsx)؛ نبود (فیلم‌های قدیمی) =
// یک باکس با کلِ فیلم.

const BOXES = 3;
const EDGE = 0.3; // برشِ نرمِ سفیدِ بینِ نماها دیده نشود

function groupAngles(segments) {
  if (!segments || !segments.length) return [{ id: 'all', label: 'فیلمِ طرح', ranges: [[0, null]] }];
  const out = [];
  segments.forEach((g) => {
    let a = out.find((x) => x.id === g.id);
    if (!a) {
      a = { id: g.id, label: g.label, ranges: [] };
      out.push(a);
    }
    const len = g.t1 - g.t0;
    a.ranges.push(len > EDGE * 4 ? [g.t0 + EDGE, g.t1 - EDGE] : [g.t0, g.t1]);
  });
  return out;
}

// یک ویدیو که فقط بازه‌های داده‌شده را پشتِ سرِ هم پخش می‌کند (و بعد از آخری، onDone)
function RangeVideo({ src, ranges, className, onDone, active = true }) {
  const ref = useRef(null);
  const idx = useRef(0);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  const key = JSON.stringify(ranges);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    el.muted = true;
    idx.current = 0;
    let raf = 0;
    let visible = true;
    const go = (i) => {
      idx.current = i;
      const [a] = ranges[i];
      if (Math.abs(el.currentTime - a) > 0.05) el.currentTime = a;
    };
    const check = () => {
      const [, b] = ranges[idx.current];
      const end = b == null ? el.duration || Infinity : b;
      if (el.currentTime >= end - 0.03) {
        const next = idx.current + 1;
        if (next >= ranges.length) {
          if (doneRef.current) doneRef.current();
          go(0);
        } else go(next);
      }
      raf = requestAnimationFrame(check);
    };
    const start = () => {
      go(0);
      if (active && visible) el.play().catch(() => {});
    };
    if (el.readyState >= 1) start();
    else el.addEventListener('loadedmetadata', start, { once: true });
    raf = requestAnimationFrame(check);
    // بیرون از صفحه = مکث (باتری)
    let io = null;
    if (typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver(([e]) => {
        visible = e.isIntersecting;
        if (visible && active) el.play().catch(() => {});
        else el.pause();
      });
      io.observe(el);
    }
    return () => {
      cancelAnimationFrame(raf);
      if (io) io.disconnect();
      el.removeEventListener('loadedmetadata', start);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, key, active]);

  return <video ref={ref} className={className} src={src} muted playsInline preload="auto" loop={ranges.length === 1 && ranges[0][1] == null} />;
}

// یک باکس: نماهای سهمِ خودش را به نوبت
function FilmBox({ src, angles, onExpand }) {
  const [k, setK] = useState(0);
  const angle = angles[k % angles.length];
  return (
    <div className="film-boxes__box">
      <RangeVideo
        src={src}
        ranges={angle.ranges}
        className="film-boxes__video"
        onDone={angles.length > 1 ? () => setK((x) => (x + 1) % angles.length) : null}
      />
      <span className="film-boxes__label">{angle.label}</span>
      <button type="button" className="film-boxes__expand" onClick={() => onExpand(angle)} aria-label="بزرگ‌نمایی">
        <Expand size={14} strokeWidth={2.2} />
      </button>
    </div>
  );
}

export default function FilmBoxes({ src, segments }) {
  const angles = useMemo(() => groupAngles(segments), [segments]);
  const [big, setBig] = useState(null);
  const n = Math.min(BOXES, angles.length);
  const shares = Array.from({ length: n }, (_, i) => angles.filter((_, j) => j % n === i));
  useEffect(() => {
    if (!big) return undefined;
    const onKey = (e) => e.key === 'Escape' && setBig(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [big]);
  return (
    <>
      <div className={`film-boxes is-${n}`}>
        {shares.map((list, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <FilmBox key={i} src={src} angles={list} onExpand={setBig} />
        ))}
      </div>
      {big && (
        <div className="film-boxes__modal" onClick={() => setBig(null)}>
          <div className="film-boxes__modal-body" onClick={(e) => e.stopPropagation()}>
            <RangeVideo src={src} ranges={big.ranges} className="film-boxes__modal-video" />
            <div className="film-boxes__modal-bar">
              <span>{big.label}</span>
              <button type="button" onClick={() => setBig(null)} aria-label="بستن">
                <X size={18} strokeWidth={2.2} />
              </button>
            </div>
            {angles.length > 1 && (
              <div className="film-boxes__modal-tabs">
                {angles.map((a) => (
                  <button key={a.id} type="button" className={a.id === big.id ? 'is-on' : ''} onClick={() => setBig(a)}>
                    {a.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
